// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { SearchPageExactProductResults } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/__internal__/SearchPageResults/__internal__/SearchPageProductResults/__internal__/SearchPageExactProductResults/SearchPageExactProductResults";
import { useSearchPageSlicedProducts } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/useSearchPageSlicedProducts";
import { useDiscoverPageFilteredProductGrids } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/DataFetching/Products/useDiscoverPageFilteredProductGrids";
import { useSearchPageParsedSearch } from "@faire/retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageParsedSearch";
import { useDiscoverPageShouldShowTopProducts } from "@faire/retailer-visitor-shared/views/search-v2/util/useDiscoverPageShouldShowTopProducts";
import { RenderWithBuilder } from "@faire/web--source/builder/BuilderComponents/RenderWithBuilder";
import { useAssignSettingBuilderXp1WithBooleanType } from "@faire/web--source/common/settings/useSettingBuilderExperiments";
import { Button } from "@faire/web--source/slate/Button";
import { Spacer } from "@faire/web--source/slate/spacing";
import { ResponsiveValues } from "@faire/web--source/slate/Theme/ResponsiveValues";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackSeoDiscoverPageFilteredProductGridsSeeAllClick } from "@faire/web-api--source/events/seo/click/discoverPageFilteredProductGridsSeeAll";
import { trackSeoDiscoverPageFilteredProductGridsViewView } from "@faire/web-api--source/events/seo/view/discoverPageFilteredProductGridsView";
import React from "react";
import { useLocation } from "react-router-dom";

export const MAX_PRODUCTS_PER_FILTERED_SECTION: ResponsiveValues<number> = {
  mobileAndAbove: 8,
  desktopAndAbove: 10,
  xLargeAndAbove: 12,
};

export const DiscoverPageFilteredProductGrids = () => {
  const { pathname } = useLocation();
  const filteredProductGrids = useDiscoverPageFilteredProductGrids();
  const query = useSearchPageParsedSearch().q;
  const queryProducts = useSearchPageSlicedProducts();
  const itemsPerFilteredSection = useResponsiveValue(
    MAX_PRODUCTS_PER_FILTERED_SECTION
  );
  const shouldShowTopProducts = useDiscoverPageShouldShowTopProducts();
  const topProducts = queryProducts?.slice(0, itemsPerFilteredSection);

  const handleSeeAllClick = (title: string, ctaLink: string) => {
    trackSeoDiscoverPageFilteredProductGridsSeeAllClick(
      pathname,
      title,
      ctaLink
    );
  };

  if (filteredProductGrids.length === 0 && !shouldShowTopProducts) {
    return null;
  }

  return (
    <>
      <Spacer
        height={{
          mobileAndAbove: "3x",
          desktopAndAbove: "5x",
        }}
      />
      {shouldShowTopProducts ? (
        <>
          <Typography
            className="md:!pl-2"
            variant={{
              mobileAndAbove: "pageHeaderSerifRegular",
              tabletAndAbove: "displaySSerifRegular",
            }}
          >
            <StrictLocalMsg
              defaultMessage="Top products in {queryFullyTranslatedSentence}"
              description={{
                text: "Header for a product grid",
                img: "https://cdn.faire.com/fastly/64f56536549126e54f02232488f54398578b4c2030b029938da5b27a8cab41b7.png",
              }}
              values={{
                queryFullyTranslatedSentence: query ?? "",
              }}
            />
          </Typography>
          <Spacer height="3x" />
          <SearchPageExactProductResults
            title={query ?? ""}
            products={topProducts ?? []}
            irrelevantProducts={[]}
            bannerData={[]}
          />
          <Spacer height="6x" />
          <ProductGridBanner />
          <Spacer height="6x" />
        </>
      ) : null}
      {filteredProductGrids.map((filteredProductGrid, index) => (
        <ViewEventWrapper
          key={filteredProductGrid.title}
          title={filteredProductGrid.title ?? ""}
          position={index}
        >
          {filteredProductGrid.cta_link ? (
            <div className="absolute top-1.5 right-2 md:top-4">
              <Button
                variant="tertiary"
                linkTo={filteredProductGrid.cta_link}
                onClick={() =>
                  handleSeeAllClick(
                    filteredProductGrid.title ?? "",
                    filteredProductGrid.cta_link ?? ""
                  )
                }
              >
                <StrictLocalMsg
                  defaultMessage="See all"
                  description={{
                    text: "CTA button to click on to see all products in a category",
                    img: "https://cdn.faire.com/fastly/c5d5837c88dd84ca7abbfab839335377bdc5acaa15771b4e687923176f3650f0.png",
                  }}
                />
              </Button>
            </div>
          ) : null}
          <Typography
            className="md:pl-2!"
            variant={{
              mobileAndAbove: "pageHeaderSerifRegular",
              tabletAndAbove: "displaySSerifRegular",
            }}
          >
            {filteredProductGrid.title}
          </Typography>
          <Spacer height="3x" />
          <SearchPageExactProductResults
            title={filteredProductGrid.title ?? ""}
            products={filteredProductGrid.product_tiles.slice(
              0,
              itemsPerFilteredSection
            )}
            irrelevantProducts={[]}
            bannerData={[]}
            brandMap={filteredProductGrid.brand_by_token}
          />
          <Spacer height="6x" />
          {index === 0 && !shouldShowTopProducts ? (
            <>
              <ProductGridBanner />
              <Spacer height="6x" />
            </>
          ) : null}
        </ViewEventWrapper>
      ))}
      <Typography
        className="pl-2!"
        variant={{
          mobileAndAbove: "pageHeaderSerifRegular",
          tabletAndAbove: "displaySSerifRegular",
        }}
      >
        <StrictLocalMsg
          defaultMessage="All products in {queryFullyTranslatedSentence}"
          description={{
            text: "Header for all products matching a search query",
            img: "https://cdn.faire.com/fastly/5ca8a21a946143fb6b23ba5fd3cba8632dec3f411399acbb8c9dce3c00ef4106.png",
          }}
          values={{
            queryFullyTranslatedSentence: query ?? "",
          }}
        />
      </Typography>
      <Spacer height="1x" />
    </>
  );
};

const ViewEventWrapper: React.FC<{
  title: string;
  position: number;
  children: React.ReactNode;
}> = ({ title, position, children }) => {
  const { pathname } = useLocation();
  const viewRef = useDoActionWhenVisible(() => {
    trackSeoDiscoverPageFilteredProductGridsViewView(
      pathname,
      title,
      `${position}`
    );
  });

  return (
    <div className="relative" ref={viewRef}>
      {children}
    </div>
  );
};

const ProductGridBanner = () => {
  const location = useLocation();
  const assignSettingBuilderXp1WithBooleanType =
    useAssignSettingBuilderXp1WithBooleanType();
  return (
    <RenderWithBuilder
      modelName="discover-sign-up-banner"
      data={{
        signupUrl: `${location}?signUp=1`,
      }}
      onViewEvent={assignSettingBuilderXp1WithBooleanType}
    />
  );
};
